<template>
  <main class="welcome-page">
    <h1 class="welcome-page__title">Добро пожаловать в&nbsp;МИС&nbsp;32top!</h1>
    <div class="welcome-page__dashboard">
      <router-link
        :to="panel.url"
        class="welcome-page__dashboard__panel"
        v-for="panel in dashboard"
        :key="panel.title"
        tag="div"
      >
        <p class="welcome-page__dashboard__panel__title">{{ panel.title }}</p>
        <div class="welcome-page__dashboard__periods">
          <div class="welcome-page__dashboard__period" v-for="period in panel.periods" v-bind:key="period.title">
            <p class="welcome-page__dashboard__period__title">{{ period.title }}</p>
            <div class="welcome-page__dashboard__period__statistic">
              <span class="welcome-page__dashboard__period__value">
                {{ period.value }}
              </span>
              <div class="welcome-page__dashboard__period__difference">
                <the-tooltip
                  v-bind:is-disabled="isTooltipDisabled(period.tags)"
                  position="bottom"
                >
                  <template v-slot:trigger="{ listenerObject }">
                    <span
                      v-on="listenerObject"
                      class="welcome-page__dashboard__period__value-diff"
                      v-bind:class="period.tags"
                    >
                      {{ period.diff }}
                    </span>
                  </template>
                  {{ period.tooltip }}
                </the-tooltip>
              </div>
            </div>
          </div>
        </div>
      </router-link>
    </div>
    <div class="welcome-page__feed">
      <div
        class="welcome-page__feed-day"
        v-for="({ date, items }) in feed"
        v-bind:key="date">
        <time class="welcome-page__feed-day__date">{{ date }}</time>
        <div>
          <div class="welcome-page__feed-item" v-for="item in items" v-bind:key="item.id">
            <div>
              <h3>{{ item.title }}</h3>
              <div v-html="item.description" class="ql-editor welcome-page__feed-item__description" />
              <div class="welcome-page__feed-item__vote">
                <button
                  type="button"
                  class="welcome-page__feed-item__vote__button like"
                  v-bind:class="{ active: $options.voteLike === item.vote }"
                  v-bind:disabled="$options.voteLike === item.vote"
                  v-on:click="onUpdateVote(item.id, $options.voteLike)" />
                <button
                  type="button"
                  class="welcome-page__feed-item__vote__button dislike"
                  v-bind:class="{ active: $options.voteDislike === item.vote }"
                  v-bind:disabled="$options.voteDislike === item.vote"
                  v-on:click="onUpdateVote(item.id, $options.voteDislike)" />
              </div>
            </div>
            <button
              type="button"
              class="welcome-page__feed-item__button-show"
              v-on:click="onShowUpdate(item.id)"
            />
          </div>
        </div>
      </div>
    </div>
    <modal-dialog
      v-bind:title="updateProps.title"
      v-bind:value="isShowUpdate"
      v-on:input="onCloseUpdateModal"
      width="80dvw"
      max-width="700"
      title-large
      with-cross
    >
      <div v-if="isShowUpdate" class="welcome-page__modal-wrapper">
        <div class="welcome-page__modal">
          <div class="welcome-page__modal__info ql-container ql-snow" style="border-width: 0;">
            <zoom-image
              v-if="isShowUpdateImage"
              class="welcome-page__modal__button-img"
              :src="updateProps.photoUrl"
              :alt="updateProps.title" />
            <div v-html="updateProps.description" class="welcome-page__modal__text ql-editor" />
          </div>
          <button
            v-if="updateProps.linkAnchor"
            type="button"
            class="welcome-page__modal__link-anchor"
            @click="onRedirect(updateProps.linkAnchor)">
            Перейти
          </button>
          <form
            class="welcome-page__modal__form"
            v-on:submit.prevent="onStoreComment"
          >
            <textarea-length-limitation
              class="welcome-page__modal__input"
              v-model="comment"
              required
              title="Комментарий"
              v-bind:max-length="1000"
              rows="2"
              placeholder="Комментарий"
            />
            <div class="welcome-page__modal__buttons">
              <button
                class="welcome-page__modal__button-submit"
                type="submit"
                v-bind:disabled="isSubmitCommentButtonDisabled"
              >
                Отправить
              </button>
            </div>
          </form>
        </div>
      </div>
    </modal-dialog>
  </main>
</template>

<script>
import notifierMixin from '@/mixins/notifierMixin';
import ModalDialog from '@/components/ModalDialog.vue';
import { Maybe } from 'monet';
import TextareaLengthLimitation from '@/components/TextareaLengthLimitation.vue';
import ZoomImage from '@/components/ZoomImage.vue';
import TheTooltip from '@/components/TheTooltip.vue';

/**
 * @description Приветственная страница.
 * @type {Vue}
 */
export default {
  voteLike: 'like',
  voteDislike: 'dislike',
  components: {
    ModalDialog,
    TextareaLengthLimitation,
    ZoomImage,
    TheTooltip,
  },
  created() {
    const onFail = (error) => {
      this.notifyFail(error.title);
    };
    const onSuccess = (response) => {
      this.dashboard = response.dashboard;
      this.feed = response.feed;
    };
    this.$userGateway.viewWelcome().then((result) => result.fold(onFail, onSuccess));
  },
  computed: {
    updateProps() {
      return this.selectedUpdate.fold(({ title: '' }))((props) => props);
    },
    isShowUpdate() {
      return this.selectedUpdate.isSome();
    },
    isShowUpdateImage() {
      return this.selectedUpdate.fold(false)(({ photoUrl }) => !!photoUrl);
    },
    isSubmitCommentButtonDisabled() {
      return this.isAwaitRequest || this.comment.length === 0;
    },
  },
  data() {
    return {
      comment: '',
      dashboard: [],
      feed: [],
      isAwaitRequest: false,
      isShown: false,
      selectedUpdate: Maybe.none(),
    };
  },
  methods: {
    onStoreComment() {
      this.isAwaitRequest = true;
      const updateId = this.updateProps.id;
      const onFail = (error) => {
        this.notifyFail(error.title);
      };
      const onSuccess = (message) => {
        this.notifySuccess(message);
        this.comment = '';
        this.selectedUpdate = Maybe.none();
      };
      this
        .$updatesGateway
        .storeComment(updateId, this.comment)
        .then((result) => result.fold(onFail, onSuccess))
        .finally(() => { this.isAwaitRequest = false; });
    },
    onShowUpdate(updateId) {
      const onFail = (error) => {
        this.notifyFail(error.title);
      };
      const onSuccess = (response) => {
        this.comment = '';
        this.selectedUpdate = Maybe.fromEmpty(response);
      };
      this
        .$updatesGateway
        .viewById(updateId)
        .then((result) => result.fold(onFail, onSuccess));
    },
    onCloseUpdateModal() {
      this.selectedUpdate = Maybe.none();
    },
    /**
     * @description Обновить оценку по обновлению.
     * @param {number} updateId
     * @param {string} vote
     * @returns {void}
     */
    onUpdateVote(updateId, vote) {
      this
        .$updatesGateway
        .rateUpdate(updateId, vote)
        .then((result) => {
          result.forEach(({ title }) => {
            this.notifySuccess(title);
            this.feed = this.feed.map(
              (release) => ({
                ...release,
                items: release.items.map(
                  (item) => ({ ...item, vote: item.id === updateId ? vote : item.vote }),
                ),
              }),
            );
          });
          result.forEachLeft(({ title }) => {
            this.notifyFail(title);
          });
        });
    },
    /**
     * @description Обработать событие перенаправления
     * @param link {string}
     * @returns {void}
     */
    onRedirect(link) {
      const isHasPath = this
        .$router
        .options
        .routes
        .filter((route) => route.path === link)
        .length > 0;
      const isSamePath = this.$route.path !== link;
      if (isHasPath && isSamePath) {
        this.$router.push({ path: link });
      }
    },
    /**
     * @description Деактивировать показ подсказки.
     * @param {string[]} tags
     * @returns {bool}
     */
    isTooltipDisabled(tags) {
      return tags.includes('equals');
    },
  },
  mixins: [notifierMixin],
  name: 'ClinicPanelWelcome',
};
</script>

<style lang="scss" scoped>
@use "@/scss/buttons";
@import "@/scss/quillEditorLib.css";

.welcome-page {
  display: flex;
  flex-direction: column;
  row-gap: 10px;
  width: 100%;
  padding: 36px;
  overflow: auto;
  &__title {
    font-size: 32px;
    line-height: 100%;
  }
  &__dashboard {
    margin-top: 20px;
    display: flex;
    gap: 20px;
    &__panel {
      width: min(100%, 415px);
      display: flex;
      flex-direction: column;
      gap: 18px;
      padding: 30px 20px;
      background: #F2F5F8;
      border-radius: 6px;
      cursor: pointer;
      &__title {
        color: var(--reading-color);
        font-weight: 500;
        font-size: 18px;
      }
    }
    &__periods {
      display: flex;
      flex-wrap: wrap;
      justify-content: space-between;
      gap: 14px;
    }
    &__period {
      flex: 1;
      display: flex;
      flex-direction: column;
      gap: 4px;
      &__title {
        font-weight: 400;
        font-size: 14px;
        color: #707683;
      }
      &__statistic {
        display: flex;
        align-items: flex-start;
        column-gap: 5px;
      }
      &__value {
        font-weight: 600;
        font-size: 22px;
        white-space: nowrap;
        color: #192A3E;
      }
      &__value-diff {
        font-size: 14px;
        color: #192A3E;
        white-space: nowrap;
        &.up {
          color: #29A34B;
        }
        &.down {
          color: #EF474C;
        }
      }
    }
  }
  &__feed {
    max-width: 1285px;
    margin-block: 20px;
    overflow-y: auto;
    &-day {
      display: grid;
      grid-template-columns: 100px 1fr;
      column-gap: 30px;
      padding-bottom: 10px;
      &:not(:last-child) {
        border-bottom: 1px solid rgba(#c2cfe0, 0.6);
      }
      &__date {
        padding-top: 20px;
        font-weight: 500;
        font-size: 18px;
        color: #0489E0;
      }
    }
    &-item {
      display: flex;
      column-gap: 10px;
      align-items: center;
      justify-content: space-between;
      width: 100%;
      padding: 20px;
      &:not(:only-child, :last-child) {
        border-bottom: 1px solid rgba(#c2cfe0, 0.4);
      }
      h3 {
        color: var(--reading-color);
        font-weight: 500;
        font-size: 18px;
      }
      &__description.ql-editor {
        margin-top: 6px;
        color: #707683;
        font-weight: 400;
        font-size: 16px;
        padding: 0 50px 0 0 !important;
        overflow-wrap: anywhere;
        ::v-deep p {
          display: block;
        }
      }
      &__button {
        &-show {
          flex-shrink: 0;
          width: 52px;
          height: 52px;
          background-image: url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNTIiIGhlaWdodD0iNTIiIHZpZXdCb3g9IjAgMCA1MiA1MiIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjUyIiBoZWlnaHQ9IjUyIiByeD0iMjYiIGZpbGw9IiNEQUYwRkYiLz4KPHBhdGggZD0iTTIwLjgzMzQgMTUuNUwzNy4xNjY3IDI2TDIwLjgzMzQgMzYuNVYxNS41WiIgc3Ryb2tlPSIjMDQ4OUUwIiBzdHJva2Utd2lkdGg9IjIiIHN0cm9rZS1saW5lY2FwPSJyb3VuZCIgc3Ryb2tlLWxpbmVqb2luPSJyb3VuZCIvPgo8L3N2Zz4K');
        }
      }
      &__vote {
        margin-top: 10px;
        display: flex;
        flex-direction: row;
        align-items: center;
        gap: 10px;
        &__button {
          cursor: pointer;
          background-size: contain;
          width: 21px;
          height: 20px;
          &.like {
            background-image: url("@/assets/img/like-grey.svg");
            &.active,
            &:hover {
              background-image: url("@/assets/img/like-green.svg");
            }
          }
          &.dislike {
            background-image: url("@/assets/img/dislike-grey.svg");
            &.active,
            &:hover {
              background-image: url("@/assets/img/dislike-red.svg");
            }
          }
        }
      }
    }
  }
  &__modal {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    row-gap: 25px;
    height: 100%;
    padding: 10px;
    text-align: left;
    ::v-deep p {
      display: block;
      font-size: inherit;
    }
    &-wrapper {
      height: 100%;
    }
    &__input {
      width: 100%;
    }
    &__text.ql-editor {
      height: fit-content;
      padding: 12px 0 !important;
      font-size: 16px;
      overflow-wrap: anywhere;
    }
    &__form {
      display: flex;
      flex-direction: column;
      gap: 10px;
    }
    &__buttons {
      display: flex;
      gap: 25px;
      align-items: center;
    }
    &__button-submit {
      @include buttons.btn-blue(150px, 2.5rem, 1rem);
      &:disabled {
        background-color: var(--unfocus-color);
      }
    }
    &__button-img {
      width: 100%;
      height: auto;
      overflow: hidden;
      ::v-deep img {
        padding: 0;
        max-height: 100%;
        object-fit: contain;
      }
    }
    &__link-anchor {
      flex-shrink: 0;
      text-align: center;
      height: 50px;
      margin-inline: auto;
      padding: 10px;
      width: 220px;
      font-size: 18px;
      text-decoration: none;
      color: white;
      background-color: #1F85C8;
      border: solid 1px transparent;
      border-radius: 15px;
      transition: 0.5s;
      &:hover {
        color: #1F85C8;
        background-color: white;
        border: solid #1F85C8 1px;
      }
    }
    &__info {
      padding: 10px;
      max-height: 510px;
      overflow-y: auto;
      &__title {
        font-weight: bold;
        font-size: 24px;
      }
    }
  }
}

@media screen and (max-width: 768px) {
  .welcome-page {
    &__modal {
      padding-inline: 0;
      &__info {
        padding: 0;
      }
      &__button-img {
        height: fit-content;
      }
    }
    &__dashboard {
      flex-wrap: wrap;
      &__panel {
        width: 48%;
      }
    }
  }
}

@media screen and (max-width: 620px) {
  .welcome-page {
    &__dashboard {
      flex-direction: column;
      &__panel {
        width: 100%;
      }
    }
    &__feed {
      padding-block: 15px;
    }
    &__feed-day {
      grid-template-columns: 1fr;
      margin-bottom: 20px;
      &__date {
        padding-top: 0;
      }
    }
    &__feed-item {
      align-items: flex-start;
      padding-inline: 0;
      &__description.ql-editor {
        padding-right: 20px !important;
      }
    }
  }
}

@media screen and (max-width: 480px) {
  .welcome-page__modal {
    &-wrapper {
      max-height: 460px;
    }
    &__buttons {
      justify-content: space-between;
    }
    &__buttons-control {
      flex-direction: column;
      row-gap: 15px;
      padding-inline: 20px;
    }
    &__buttons-actions {
      gap: 10px;
    }
  }
}
</style>
